import { VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import React from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum, PodeNaoPodeEnum } from '../../model-consultaSeguimento'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta18ExemplosEnum,
  ConsultaSeguimentoPergunta18Model,
} from './model-consultaSeguimentoPergunta18'
import { isPergunta18ExemplosAllNao } from './utils-consultaSeguimentoPergunta18'

interface ConsultaSeguimentoPergunta18FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta18Model>
}

export function ConsultaSeguimentoPergunta18Field(props: Readonly<ConsultaSeguimentoPergunta18FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta18Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta18Model>({
    name,
    calculateResultado,
    conditionToCalculate,
  })

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_18}
          title={
            <ConsultaSeguimentoPerguntaHeaderTitle sufix=' compreende quando você diz a ele para fazer alguma coisa?' />
          }
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_18_HEADER} />}
        />
        <VFlow vSpacing={0}>
          {!isEmpty(value?.PERGUNTA_18_HEADER) && <ConsultaSeguimentoIndicatorStart />}

          {value?.PERGUNTA_18_HEADER === SimNaoEnum.SIM && (
            <VFlow vSpacing={0.5}>
              <ConsultaSeguimentoIndicatorHelper title='Por favor, dê um exemplo de como ele compreendeu você.' />
              <RadioGroupBoxField
                name={name.PERGUNTA_18_PODE_NAO_PODE}
                label='O exemplo indica que:'
                options={PERGUNTA_18_PODE_NAO_PODE_OPTIONS}
                required
              />
            </VFlow>
          )}

          {(value?.PERGUNTA_18_HEADER === SimNaoEnum.NAO ||
            value.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.NAO_PODE) && (
            <VFlow vSpacing={0}>
              {value?.PERGUNTA_18_HEADER === SimNaoEnum.SIM &&
                value?.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.NAO_PODE && <ConsultaSeguimentoArrowDown />}
              <PerguntaSimNaoRadioGroupBoxField
                name={name.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM}
                label={
                  <>
                    Quando ele/ela tem uma dica, ele consegue seguir a ordem? <br />
                    Por exemplo, quando você está vestido para sair e diz a ele para pegar o seu sapato, ele entende?
                  </>
                }
                required
              />
              {value?.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.NAO && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoArrowDown />
                  <PerguntaSimNaoRadioGroupBoxField
                    name={name.PERGUNTA_18_SENTA_A_MESA}
                    label='Se é hora do jantar e a comida está na mesa, e você diz ao seu filho para se sentar, ele se senta à mesa?'
                    required
                  />
                </VFlow>
              )}
              {(value?.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.SIM ||
                value?.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.SIM) && (
                <VFlow>
                  <ConsultaSeguimentoArrowDown />
                  <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta18ExemplosEnum>
                    name={name.PERGUNTA_18_EXEMPLOS}
                    label={
                      <>
                        Quando a situação não dá nenhuma dica, ele consegue seguir uma ordem? <br />
                        Por exemplo...
                      </>
                    }
                    description='Pergunte até obter um sim ou use todos os exemplos:'
                    required
                    options={PERGUNTA_18_EXEMPLOS_OPTIONS}
                  />
                </VFlow>
              )}
            </VFlow>
          )}
        </VFlow>
      </VFlow>
      {value?.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU:
              value?.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.NAO &&
              value?.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.NAO
                ? ''
                : 'Não para todas:',
            PASSOU:
              value?.PERGUNTA_18_HEADER === SimNaoEnum.SIM && value?.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.PODE
                ? ''
                : 'Sim para qualquer uma:',
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_18_PODE_NAO_PODE_OPTIONS: RadioOption<PodeNaoPodeEnum>[] = [
  {
    label: (
      <>
        A criança <strong>pode</strong> compreender uma ordem simples, sem sinais não-verbais.
      </>
    ),
    value: PodeNaoPodeEnum.PODE,
  },
  {
    label: (
      <>
        A criança <strong>não pode</strong> compreender uma ordem simples, sem sinais não-verbais.
      </>
    ),
    value: PodeNaoPodeEnum.NAO_PODE,
  },
]

const PERGUNTA_18_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta18ExemplosEnum>[] = [
  {
    label:
      'Se você diz: “Me mostre o seu sapato” sem apontar, fazendo gestos, ou dando sugestões (quando você não está saindo ou se vestindo), o seu filho mostra o sapato?',
    value: ConsultaSeguimentoPergunta18ExemplosEnum.SEM_DICA_MOSTRA_SAPATO,
  },
  {
    label:
      'Se você diz: “Me traz o cobertor” ou pede outro objeto sem apontar, sem fazer gestos, ou sem dar sugestões, o seu filho traz para você?',
    value: ConsultaSeguimentoPergunta18ExemplosEnum.SEM_DICA_TRAZ_COBERTOR,
  },
  {
    label:
      'Se você diz: “Põe o livro na cadeira” sem apontar, sem fazer gestos, ou sem dar quaisquer outras sugestões, o seu filho coloca o livro na cadeira?',
    value: ConsultaSeguimentoPergunta18ExemplosEnum.SEM_DICA_POE_LIVRO_CADEIRA,
  },
]

const calculateResultado = (values: ConsultaSeguimentoPergunta18Model) => {
  if (values.PERGUNTA_18_HEADER === SimNaoEnum.SIM && values.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.PODE)
    return PassouFalhouEnum.PASSOU

  if (
    ((values.PERGUNTA_18_HEADER === SimNaoEnum.SIM && values.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.NAO_PODE) ||
      values.PERGUNTA_18_HEADER === SimNaoEnum.NAO) &&
    values.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.NAO &&
    values.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.NAO
  )
    return PassouFalhouEnum.FALHOU

  if (
    values.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.SIM ||
    values.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.SIM
  ) {
    if (hasAnySim(values.PERGUNTA_18_EXEMPLOS)) {
      return PassouFalhouEnum.PASSOU
    } else if (isPergunta18ExemplosAllNao(values.PERGUNTA_18_EXEMPLOS)) {
      return PassouFalhouEnum.FALHOU
    }
  }
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta18Model): boolean => {
  const isHeaderSimEPode =
    values.PERGUNTA_18_HEADER === SimNaoEnum.SIM && values.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.PODE

  if (isHeaderSimEPode) {
    return true
  }

  const isHeaderNaoOuHeaderSimENaoPode =
    values.PERGUNTA_18_HEADER === SimNaoEnum.NAO ||
    (values.PERGUNTA_18_HEADER === SimNaoEnum.SIM && values.PERGUNTA_18_PODE_NAO_PODE === PodeNaoPodeEnum.NAO_PODE)

  if (!isHeaderNaoOuHeaderSimENaoPode) {
    return false
  }

  const isSegueOrdemNaoESentaMesaNao =
    values.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.NAO && values.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.NAO

  if (isSegueOrdemNaoESentaMesaNao) {
    return true
  }

  const isSegueOrdemSimOuSegueOrdemNaoESentaMesaSim =
    values.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.SIM ||
    (values.PERGUNTA_18_TEM_DICA_SEGUE_ORDEM === SimNaoEnum.NAO && values.PERGUNTA_18_SENTA_A_MESA === SimNaoEnum.SIM)

  if (isSegueOrdemSimOuSegueOrdemNaoESentaMesaSim) {
    return hasAnySim(values.PERGUNTA_18_EXEMPLOS) || isPergunta18ExemplosAllNao(values.PERGUNTA_18_EXEMPLOS)
  }

  return false
}
